import { create } from "zustand";
import moment from "moment";
import { usePaymentChartData, useUsageChartData } from "./use-user-chart-data";

const defaultFilter = {
  email: "",
  countryCode: "",
  role: [],          // ['free','pre']
  source: [],        // ['manual-invoice', ...]
  isUsages: null,    // true | false | null
  isAfter: null,     // false (expired) | true (not expired) | null
  isFirstLogin: null,     // false (no login) | true (first login) | null
  dateRange: null,   // {from: ISO, to: ISO} | null
};

const defaultSorter = {
  field: "createdAt",
  order: "descend"
};

export const useUserHistory = create()((set, get) => ({
  /** ---------- REF (non-reactive) ---------- **/
  // chứa toàn bộ users tải được: mutate in-place để KHÔNG re-render
  usersHistoryRef: [],
  // bản đã filter (full), KHÔNG set => không re-render
  filteredUsersRef: [],

  /** ---------- REACTIVE STATE (nhẹ) ---------- **/
  usersHistoryFilter: [], // chỉ page hiện tại
  totalUsers: 0, // tổng đếm sau lọc
  sorter: { ...defaultSorter },
  filterState: { ...defaultFilter },

  setUsersHistoryFilter: (value) => set({ usersHistoryFilter: value }),

  addUsersHistoryRef: (users) => {
    const store = get();
    store.usersHistoryRef.push(...users);
    // mặc định ban đầu filterRef = full data
    store.filteredUsersRef = [...store.usersHistoryRef];
    set({ totalUsers: store.usersHistoryRef.length });
  },

  /** ---------- FILTER CORE ---------- **/
  _matchUser(u, f) {
    // date range
    if (f.dateRange) {
      const d = moment(u.createdAt);
      if (d.isBefore(moment(f.dateRange.from)) || d.isAfter(moment(f.dateRange.to))) return false;
    }
    // email contains
    if (f.email) {
      const v = (u.email || "").toLowerCase();
      if (!v.includes(f.email)) return false;
    }
    // countryCode contains
    if (f.countryCode) {
      const v = (u.countryCode || "").toLowerCase();
      if (!v.includes(f.countryCode)) return false;
    }
    // role in []
    if (f.role?.length) {
      if (!f.role.includes(u.role)) return false;
    }
    // source in []
    if (f.source?.length) {
      if (!f.source.includes(u.source)) return false;
    }
    // isUsages tri-state
    if (f.isUsages !== null) {
      if ((u.isUsages === true) !== (f.isUsages === true)) return false;
    }
    // isAfter tri-state (expired = false)
    if (f.isAfter !== null) {
      if ((u.isAfter === true) !== (f.isAfter === true)) return false;
    }
    if (f.isFirstLogin !== null) {
      if (f.isFirstLogin === true && u.isFirstLogin !== true) return false;
      if (f.isFirstLogin === false && u.isFirstLogin === true) return false;
    }
    return true;
  },

  _sortInPlace(arr, sorter) {
    if (!sorter?.field || !sorter?.order) return;
    const { field, order } = sorter;
    const factor = order === "ascend" ? 1 : -1;

    if (field === "createdAt" || field === "endTime") {
      arr.sort((a, b) => (moment(a[field]).valueOf() - moment(b[field]).valueOf()) * factor);
      return;
    }
    // fallback string/number
    arr.sort((a, b) => {
      const av = a[field], bv = b[field];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * factor;
      if (bv == null) return 1 * factor;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  },

  /** Recompute filteredUsersRef (non-reactive) + cắt trang (reactive) */
  _recomputeAndSlice() {
    const store = get();
    const base = store.usersHistoryRef;
    const f = store.filterState;

    // lọc
    const filtered = [];
    for (let i = 0; i < base.length; i++) {
      if (get()._matchUser(base[i], f)) filtered.push(base[i]);
    }

    // sort
    get()._sortInPlace(filtered, store.sorter);

    // gán ref KHÔNG set
    store.filteredUsersRef = filtered;

    const { page, pageSize } = store;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    set({
      usersHistoryFilter: filtered.slice(start, end),
    });
  },

  // set hoặc merge filter, rồi recompute
  setFilter: (partial, isApply = true) => {
    const next = { ...get().filterState, ...partial };
    set({ filterState: next, page: 1 }); // reset về page 1 khi đổi filter

    if (isApply) {
      get()._recomputeAndSlice();
    }
  },

  // Khi reset 1 field
  resetFilterKey: (key, isApply = true) => {
    const cur = { ...get().filterState };
    if (key === "role" || key === "source") cur[key] = [];
    else if (key === "isUsages" || key === "isAfter" || key === "dateRange") cur[key] = null;
    else cur[key] = "";
    set({ filterState: cur, page: 1 });

    if (isApply) {
      get()._recomputeAndSlice();
    }
  },

  // Khi reset tất cả field
  resetAllFilters: (isApply = true) => {
    set({ filterState: { ...defaultFilter }, page: 1 });

    if (isApply) {
      get()._recomputeAndSlice();
    }
  },

  // sắp xếp
  setSorter: (sorter, isApply = true) => {
    set({ sorter: sorter?.order ? sorter : { field: "createdAt", order: "descend" }, page: 1 });

    if (isApply) {
      get()._recomputeAndSlice();
    }
  },

  setFilterAndSorter: ({ filter, sorter }, isApply = true) => {
    const next = { ...get().filterState, ...filter };
    set({
      filterState: next,
      sorter: sorter?.order ? sorter : { field: "createdAt", order: "descend" },
      page: 1
    });

    if (isApply) {
      get()._recomputeAndSlice();
    }
  },

  // reset filter, sorter khi truy cập trang
  resetFilterAndSorter: (isApply = true) => {
    const store = get();
    store.filteredUsersRef = [...store.usersHistoryRef];
    set({
      filterState: { ...defaultFilter },
      sorter: { ...defaultSorter },
      usersHistoryFilter: store.filteredUsersRef.slice(0, 20),
      page: 1,
    });

    if (isApply) {
      get()._recomputeAndSlice();
    }
  },

  // phân trang
  paginate: (page, pageSize) => {
    const store = get();
    set({ page, pageSize });
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    set({ usersHistoryFilter: store.filteredUsersRef.slice(start, end) });
  },

  // Sau khi fetch xong 1 batch thì gọi hàm
  applyFiltersNow: () => {
    get()._recomputeAndSlice();
  },

  // lưu danh sách các userId tại điểm ngắt giữa các lần fetch user
  addUserIdHistory: (userIds) =>
    set((state) => ({
      userIdHistory: [...state.userIdHistory, userIds],
    })),

  /** ---------- update chart ---------- **/
  updateUserById: (userId, updateData) => {
    const store = get();
    const idx = store.usersHistoryRef.findIndex(u => u.userId === userId);
    if (idx === -1) return;

    const oldUser = store.usersHistoryRef[idx];
    const newUser = { ...oldUser, ...updateData };
    store.usersHistoryRef[idx] = newUser;

    store._recomputeAndSlice();

    usePaymentChartData.getState().updatePaymentStatsByUserChange(oldUser, newUser);
  },

  removeUserById: (userId) => {
    const store = get();
    const idx = store.usersHistoryRef.findIndex(u => u.userId === userId);
    if (idx === -1) return;

    const removed = store.usersHistoryRef[idx];
    store.usersHistoryRef.splice(idx, 1);


    store._recomputeAndSlice();

    usePaymentChartData.getState().removeUserFromPaymentStats(removed);
    useUsageChartData.getState().removeUserFromUsageStats(removed);
  },

  userIdHistory: [],
  setUserIdHistory: (value) => set({ userIdHistory: value }),
  initialLoading: true,
  setInitialLoading: (value) => set({ initialLoading: value }),
  loading: false,
  setLoading: (value) => set({ loading: value }),
  isPaused: false,
  setIsPaused: (value) => set({ isPaused: value }),
  isCompleted: false,
  setIsCompleted: (value) => set({ isCompleted: value }),
  limitHistory: 500,
  setLimitHistory: (value) => set({ limitHistory: value }),
  pageHistory: 1,
  setPageHistory: (value) => set({ pageHistory: value }),
  incrementPageHistory: () => set((s) => ({ pageHistory: s.pageHistory + 1 })),
  totalPageHistory: 1,
  setTotalPageHistory: (value) => set({ totalPageHistory: value }),
  total: 0,
  setTotal: (value) => set({ total: value }),
  page: 1,
  pageSize: 20,
  setPage: (p) => set({ page: p }),
  setPageSize: (size) => set({ pageSize: size }),

  fromDate: moment().subtract(3, 'months'),
  toDate: moment(),
  email: "",
  setFromDate: (d) => set({ fromDate: d }),
  setToDate: (d) => set({ toDate: d }),
  setEmail: (v) => set({ email: v }),
  
  activeTab: "email",
  setActiveTab: (v) => set({ activeTab: v }),

  onReset: () => {
    const store = get();
    store.usersHistoryRef.length = 0; // clear in-place
    store.filteredUsersRef.length = 0; // clear in-place
    set({
      usersHistoryFilter: [],
      userIdHistory: [],
      initialLoading: true,
      isCompleted: false,
      pageHistory: 1,
      totalPageHistory: 1,
      total: 0,
      loading: false,
      isPaused: false,
      limitHistory: 500,
      totalUsers: 0,
      page: 1,
      pageSize: 20,
      sorter: { ...defaultSorter },
      filterState: { ...defaultFilter },
    });
  },
}));
